/**
 * Slack Integration Settings Page
 * 
 * Allows admins to:
 * - Connect a Slack workspace
 * - Set a default approval channel
 * - Test the Slack connection
 */

import React, { useEffect, useState } from 'react';
import MerchantHeader from '../components/MerchantHeader';
import { toast } from 'sonner';
import {
  getSlackStatus,
  registerSlackWorkspace,
  fetchSlackChannels,
  setDefaultChannel,
  testSlackPost
} from '../api/slackSettings';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  MessageSquare,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Send,
  Hash,
  Settings,
  ExternalLink
} from 'lucide-react';

// Default brand ID - should come from auth context in production
const DEFAULT_BRAND_ID = 'demo-brand';

export default function SlackSettingsPage() {
  const brandId = DEFAULT_BRAND_ID;
  
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Registration form
  const [teamId, setTeamId] = useState('');
  const [teamName, setTeamName] = useState('');
  const [botToken, setBotToken] = useState('');
  
  // Channel selection
  const [channels, setChannels] = useState([]);
  const [channelId, setChannelId] = useState('');
  const [channelName, setChannelName] = useState('');
  
  const [saving, setSaving] = useState(false);
  
  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const s = await getSlackStatus(brandId);
      setStatus(s);
      if (s.connected && s.workspace?.team_id) {
        setTeamId(s.workspace.team_id);
        setTeamName(s.workspace.team_name || '');
        if (s.default_channel?.id) {
          setChannelId(s.default_channel.id);
          setChannelName(s.default_channel.name || '');
        }
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }
  
  useEffect(() => {
    refresh();
  }, []);
  
  async function handleRegister() {
    if (!teamId || !botToken) {
      toast.error('Team ID and Bot Token are required');
      return;
    }
    
    setSaving(true);
    try {
      await registerSlackWorkspace({ teamId, brandId, botToken, teamName });
      toast.success('Slack workspace registered successfully');
      await refresh();
    } catch (e) {
      toast.error(e.message || 'Failed to register workspace');
    } finally {
      setSaving(false);
    }
  }
  
  async function handleLoadChannels() {
    if (!teamId) {
      toast.error('No workspace connected');
      return;
    }
    
    setSaving(true);
    try {
      const data = await fetchSlackChannels(teamId);
      setChannels(data.channels || []);
      toast.success(`Loaded ${data.channels?.length || 0} channels`);
    } catch (e) {
      toast.error(e.message || 'Failed to load channels');
    } finally {
      setSaving(false);
    }
  }
  
  async function handleSetDefault() {
    if (!teamId || !channelId) {
      toast.error('Select a channel first');
      return;
    }
    
    setSaving(true);
    try {
      const selected = channels.find(c => c.id === channelId);
      await setDefaultChannel({
        teamId,
        brandId,
        channelId,
        channelName: selected?.name || channelName
      });
      toast.success('Default channel saved');
      await refresh();
    } catch (e) {
      toast.error(e.message || 'Failed to set default channel');
    } finally {
      setSaving(false);
    }
  }
  
  async function handleTestPost() {
    if (!teamId || !channelId) {
      toast.error('Select a channel first');
      return;
    }
    
    setSaving(true);
    try {
      await testSlackPost({ teamId, brandId, channelId });
      toast.success('Test message sent to Slack');
    } catch (e) {
      toast.error(e.message || 'Failed to send test message');
    } finally {
      setSaving(false);
    }
  }
  
  const connected = status?.connected;
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <MerchantHeader />
        <main className="container mx-auto px-4 py-8 max-w-4xl">
          <Skeleton className="h-8 w-64 mb-4" />
          <Skeleton className="h-64 w-full" />
        </main>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-background" data-testid="slack-settings-page">
      <MerchantHeader />
      
      <main className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
            <MessageSquare className="h-8 w-8 text-primary" />
            Slack Integration
          </h1>
          <p className="text-muted-foreground mt-1">
            Route decision approvals and trace capture to Slack
          </p>
        </div>
        
        {error && (
          <Card className="mb-6 border-destructive/50 bg-destructive/10">
            <CardContent className="p-4 flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-destructive" />
              <span className="text-destructive">{error}</span>
            </CardContent>
          </Card>
        )}
        
        <div className="grid gap-6">
          {/* Connection Status */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Settings className="h-5 w-5 text-muted-foreground" />
                Connection Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-3">
                  {connected ? (
                    <>
                      <CheckCircle className="h-5 w-5 text-emerald-400" />
                      <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30">
                        Connected
                      </Badge>
                    </>
                  ) : (
                    <>
                      <AlertCircle className="h-5 w-5 text-amber-400" />
                      <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30">
                        Not Connected
                      </Badge>
                    </>
                  )}
                </div>
                
                {connected && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Workspace:</span>
                      <span className="ml-2 font-medium">
                        {status.workspace?.team_name || 'Unknown'} 
                        <span className="text-muted-foreground ml-1">
                          ({status.workspace?.team_id})
                        </span>
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Default Channel:</span>
                      <span className="ml-2 font-medium">
                        {status.default_channel?.name 
                          ? `#${status.default_channel.name}` 
                          : 'Not set'}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Pending Approvals:</span>
                      <Badge variant="secondary" className="ml-2">
                        {status.pending_approvals || 0}
                      </Badge>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
          
          {/* Registration Form */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-lg">
                {connected ? 'Update Workspace' : 'Register Workspace'}
              </CardTitle>
              <CardDescription>
                {connected 
                  ? 'Update your Slack workspace connection'
                  : 'Connect your Slack workspace to enable decision approvals'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="team-id">Team ID</Label>
                  <Input
                    id="team-id"
                    placeholder="T12345ABC"
                    value={teamId}
                    onChange={(e) => setTeamId(e.target.value)}
                    data-testid="team-id-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Find this in your Slack workspace settings
                  </p>
                </div>
                
                <div className="grid gap-2">
                  <Label htmlFor="team-name">Team Name (optional)</Label>
                  <Input
                    id="team-name"
                    placeholder="Acme Co"
                    value={teamName}
                    onChange={(e) => setTeamName(e.target.value)}
                    data-testid="team-name-input"
                  />
                </div>
                
                <div className="grid gap-2">
                  <Label htmlFor="bot-token">Bot Token</Label>
                  <Input
                    id="bot-token"
                    type="password"
                    placeholder="xoxb-..."
                    value={botToken}
                    onChange={(e) => setBotToken(e.target.value)}
                    data-testid="bot-token-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Bot User OAuth Token from your Slack app settings
                  </p>
                </div>
                
                <Button 
                  onClick={handleRegister}
                  disabled={saving || !teamId || !botToken}
                  data-testid="register-btn"
                >
                  {saving ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Register Workspace'
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
          
          {/* Default Channel */}
          {connected && (
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Hash className="h-5 w-5 text-muted-foreground" />
                  Default Approval Channel
                </CardTitle>
                <CardDescription>
                  Set a default channel for approval requests
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col md:flex-row gap-3">
                  <Button 
                    variant="outline" 
                    onClick={handleLoadChannels}
                    disabled={saving}
                    data-testid="load-channels-btn"
                  >
                    {saving ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4 mr-2" />
                    )}
                    Load Channels
                  </Button>
                  
                  <Select 
                    value={channelId}
                    onValueChange={(v) => {
                      setChannelId(v);
                      const selected = channels.find(c => c.id === v);
                      setChannelName(selected?.name || '');
                    }}
                    disabled={!channels.length}
                  >
                    <SelectTrigger className="w-full md:w-80" data-testid="channel-select">
                      <SelectValue placeholder="Select a channel..." />
                    </SelectTrigger>
                    <SelectContent>
                      {channels.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.is_private ? '🔒 ' : '#'}{c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <Button 
                    onClick={handleSetDefault}
                    disabled={saving || !channelId}
                    data-testid="save-channel-btn"
                  >
                    Save Default
                  </Button>
                  
                  <Button 
                    variant="outline"
                    onClick={handleTestPost}
                    disabled={saving || !channelId}
                    data-testid="test-post-btn"
                  >
                    <Send className="h-4 w-4 mr-2" />
                    Test
                  </Button>
                </div>
                
                {!channels.length && (
                  <p className="mt-3 text-xs text-muted-foreground">
                    Click Load Channels to see available channels, or manually paste a Channel ID.
                  </p>
                )}
              </CardContent>
            </Card>
          )}
          
          {/* Help */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-lg">Setup Guide</CardTitle>
            </CardHeader>
            <CardContent>
              <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                <li>
                  <a 
                    href="https://api.slack.com/apps" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary hover:underline inline-flex items-center gap-1"
                  >
                    Create a Slack App <ExternalLink className="h-3 w-3" />
                  </a>
                </li>
                <li>Add bot scopes: <code className="text-xs bg-muted px-1 py-0.5 rounded">commands</code>, <code className="text-xs bg-muted px-1 py-0.5 rounded">chat:write</code></li>
                <li>Install the app to your workspace</li>
                <li>Copy the Bot User OAuth Token (starts with <code className="text-xs bg-muted px-1 py-0.5 rounded">xoxb-</code>)</li>
                <li>Find your Team ID in Slack workspace settings</li>
                <li>Paste both values above and click Register</li>
              </ol>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
